import React, { useState } from 'react';
import {
  Play,
  Pause,
  Square,
  SkipForward,
  RefreshCw,
  Settings,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { usePlayMode } from '../../hooks/usePlayMode';
import { PlayModeSettings } from './PlayModeSettings';


interface PlayModeControlsProps {
  className?: string;
  compact?: boolean;
}

export const PlayModeControls: React.FC<PlayModeControlsProps> = ({
  className,
  compact = false,
}) => {
  const {
    isPlaying,
    isPaused,
    isStopped,
    stop,
    step,
    togglePlayPause,
    reloadScripts,
  } = usePlayMode();

  const [showSettings, setShowSettings] = useState(false);

  const buttonBaseClass = cn(
    'inline-flex items-center justify-center rounded transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-zinc-900',
    'disabled:opacity-40 disabled:cursor-not-allowed',
    compact ? 'p-1.5' : 'p-2'
  );

  const iconSize = compact ? 16 : 18;

  return (
    <>
      <div className={cn('flex items-center gap-1', className)}>
        {/* Play/Pause Toggle Button */}
        <button
          onClick={togglePlayPause}
          className={cn(
            buttonBaseClass,
            isPlaying
              ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
              : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 hover:text-white focus:ring-zinc-500'
          )}
          title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
        >
          {isPlaying ? (
            <Pause size={iconSize} />
          ) : (
            <Play size={iconSize} className="ml-0.5" />
          )}
        </button>

        {/* Stop Button */}
        <button
          onClick={stop}
          disabled={isStopped}
          className={cn(
            buttonBaseClass,
            'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 hover:text-white focus:ring-zinc-500'
          )}
          title="Stop (Shift+Space)"
        >
          <Square size={iconSize} />
        </button>

        {/* Step Button (only enabled when paused) */}
        <button
          onClick={step}
          disabled={!isPaused}
          className={cn(
            buttonBaseClass,
            'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 hover:text-white focus:ring-zinc-500'
          )}
          title="Step Frame (F10)"
        >
          <SkipForward size={iconSize} />
        </button>

        {/* Separator */}
        <div className="w-px h-5 bg-zinc-600 mx-1" />

        {/* Reload Scripts Button */}
        <button
          onClick={reloadScripts}
          className={cn(
            buttonBaseClass,
            'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 hover:text-white focus:ring-zinc-500'
          )}
          title="Reload Scripts (Ctrl+R)"
        >
          <RefreshCw size={iconSize} />
        </button>

        {/* Settings Button */}
        <button
          onClick={() => setShowSettings(true)}
          className={cn(
            buttonBaseClass,
            'bg-zinc-700 text-zinc-300 hover:bg-zinc-600 hover:text-white focus:ring-zinc-500'
          )}
          title="Play Mode Settings"
        >
          <Settings size={iconSize} />
        </button>
      </div>

      {/* Settings Modal */}
      <PlayModeSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

    </>
  );
};
